import { IterableMap } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { FulfillmentGroupData } from "@redotech/redo-model/fulfillments/fulfillment-group";
import {
  FulfillmentOrderLineItem,
  groupFulfillmentOrderLineItems,
} from "@redotech/redo-model/fulfillments/fulfillment-order-line-item";
import { canMergeFulfillmentGroupData } from "@redotech/redo-model/outbound-labels/can-merge-fulfillment-groups";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import {
  RedoModal,
  RedoModalProps,
} from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import {
  RedoTable,
  RedoTableProps,
} from "@redotech/redo-web/arbiter-components/tables/redo-table";
import {
  RedoTablePaddedCell,
  RedoTableTextCell,
} from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { StandardRedoTableHeaderCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-header-cells";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import { FulfillmentGroupStatusBadge } from "../../common/status-badge";
import * as styles from "./multiple-order-select-modal.module.css";

export const FulfillmentGroupSelectModal = memo(
  function FulfillmentGroupSelectModal({
    open,
    identifier,
    onClose,
    onSelect,
    relevantFulfillmentGroups,
    scannedIdentifier,
  }: {
    open: boolean;
    identifier: string;
    onClose: () => void;
    onSelect: (fulfillmentGroups: FulfillmentGroupData[]) => void;
    relevantFulfillmentGroups: FulfillmentGroupData[];
    scannedIdentifier: string;
  }) {
    const [selectedIds, setSelectedIds] = useState<Set<string>>(() => {
      const match = relevantFulfillmentGroups.find(
        (g) => g._id === scannedIdentifier,
      );
      return match ? new Set([match._id]) : new Set<string>();
    });

    const handleToggle = useHandler((groupId: string) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(groupId)) {
          next.delete(groupId);
        } else {
          next.add(groupId);
        }
        return next;
      });
    });

    const selectedCount = selectedIds.size;
    const allSelected = selectedCount === relevantFulfillmentGroups.length;

    const handleToggleAll = useHandler(() => {
      if (allSelected) {
        setSelectedIds(new Set());
      } else {
        setSelectedIds(new Set(relevantFulfillmentGroups.map((g) => g._id)));
      }
    });

    const selectedGroups = useMemo(
      () => relevantFulfillmentGroups.filter((g) => selectedIds.has(g._id)),
      [relevantFulfillmentGroups, selectedIds],
    );

    const mergeValidation = useMemo(() => {
      if (selectedCount <= 1) return { canMerge: true, reason: "" };
      return canMergeFulfillmentGroupData(selectedGroups);
    }, [selectedGroups, selectedCount]);

    const canProceed = selectedCount === 1 || mergeValidation.canMerge;

    const modalProps: RedoModalProps = {
      className: styles.modal,
      isOpen: open,
      onModalCloseRequested: onClose,
      title: `Multiple shipments found for order ${identifier}`,
      subtitle: "Select the shipments you'd like to fulfill.",
      headerBorder: true,
      footerBorder: true,
      primaryButton: {
        text:
          selectedCount <= 1
            ? "Select shipment"
            : canProceed
              ? "Merge & fulfill"
              : "Select shipments",
        disabled: selectedCount === 0 || !canProceed,
        onClick: () => {
          onSelect(selectedGroups);
        },
        size: "sm",
      },
      contentScrollable: true,
      noPadding: true,
      mainContentClassName: styles.modalContent,
      secondaryButton: { text: "Cancel", onClick: onClose, size: "sm" },
      additionalFooterContent: (
        <RedoButton
          hierarchy="tertiary"
          onClick={handleToggleAll}
          size="sm"
          text={allSelected ? "Deselect all" : "Select all"}
        />
      ),
    };

    return (
      <RedoModal {...modalProps}>
        <Flex dir="column" gap="3xl" p="3xl">
          {selectedCount > 1 && !mergeValidation.canMerge && (
            <Flex dir="column" gap="sm" px="sm">
              <RedoBadge
                color="warning"
                size="sm"
                text="These shipments cannot be merged"
              />
              <Text fontSize="xs" textColor="secondary">
                {mergeValidation.reason}. Select a single shipment to fulfill
                individually.
              </Text>
            </Flex>
          )}
          <IterableMap
            items={relevantFulfillmentGroups}
            keyFn={(item) => item._id.toString()}
          >
            {(group) => (
              <SelectListItem
                group={group}
                key={group._id.toString()}
                onToggle={handleToggle}
                selected={selectedIds.has(group._id)}
              />
            )}
          </IterableMap>
        </Flex>
      </RedoModal>
    );
  },
);

const SelectListItem = memo(function SelectListItem({
  group,
  onToggle,
  selected,
}: {
  group: FulfillmentGroupData;
  onToggle: (groupId: string) => void;
  selected: boolean;
}) {
  const getUniqueLineItems = (group: FulfillmentGroupData) => {
    const lineItems = group.items;
    return groupFulfillmentOrderLineItems(lineItems);
  };

  const tableProps: RedoTableProps<FulfillmentOrderLineItem> = {
    layout: {
      columns: [
        {
          renderHeaderCell: () => (
            <StandardRedoTableHeaderCell headerText="Item" />
          ),
          renderNormalCell: ({ row }) => (
            <RedoTablePaddedCell>
              <Flex align="center" gap="sm">
                <img
                  alt={row.title}
                  className={styles.image}
                  src={row.imageUrl ?? ""}
                />
                <Flex dir="column" gap="none">
                  <Text
                    fontSize="xs"
                    textColor="tertiary"
                    textOverflow="ellipsis"
                  >
                    {row.productTitle}
                  </Text>
                  <Text
                    fontSize="xxs"
                    textColor="secondary"
                    textOverflow="ellipsis"
                  >
                    {row.variantTitle}
                  </Text>
                </Flex>
              </Flex>
            </RedoTablePaddedCell>
          ),
        },
        {
          renderHeaderCell: () => (
            <StandardRedoTableHeaderCell headerText="SKU" />
          ),
          renderNormalCell: ({ row }) => (
            <RedoTableTextCell text={row.sku ?? ""} />
          ),
          widthMode: { type: "fixed", width: "150px" },
        },
        {
          renderHeaderCell: () => (
            <StandardRedoTableHeaderCell headerText="Price" />
          ),
          renderNormalCell: ({ row }) => (
            <RedoTableTextCell text={`$${row.totalPrice.amount}`} />
          ),
          widthMode: { type: "fixed", width: "100px" },
        },
        {
          renderHeaderCell: () => (
            <StandardRedoTableHeaderCell headerText="Quantity" />
          ),
          renderNormalCell: ({ row }) => (
            <RedoTableTextCell text={row.quantity.toString()} />
          ),
          widthMode: { type: "fixed", width: "100px" },
        },
      ],
    },
    rowsOfData: getUniqueLineItems(group),
  };

  return (
    <Card
      className={`${styles.card} ${selected ? styles.cardSelected : ""}`}
      onClick={() => onToggle(group._id)}
    >
      <Flex dir="column" gap="md">
        <Flex justify="space-between">
          <Flex data-testid={`fulfillment-group-${group._id}`}>
            <RedoCheckbox
              setValue={() => onToggle(group._id)}
              value={selected}
            />
            <Text textColor="secondary">
              {group.orders.map((order) => order.name).join(", ")}
            </Text>
          </Flex>
          <FulfillmentGroupStatusBadge fulfillmentGroupStatus={group.status} />
        </Flex>
        <Flex justify="space-between">
          <Flex dir="column" gap="none">
            <Text fontSize="xs" textColor="tertiary">
              Customer name
            </Text>
            <Text fontSize="xs" textColor="primary">
              {group.customer?.name}
            </Text>
          </Flex>
          <Flex dir="column" gap="none">
            <Text fontSize="xs" textColor="tertiary">
              Shipping address
            </Text>
            <Flex dir="column" gap="none">
              <Text fontSize="xs" textColor="primary">
                {group.destinationAddress?.address1}
              </Text>
              {group.destinationAddress?.address2 && (
                <Text fontSize="xs" textColor="primary">
                  {group.destinationAddress?.address2}
                </Text>
              )}
              <Text fontSize="xs" textColor="primary">
                {group.destinationAddress?.city},{" "}
                {group.destinationAddress?.province}
              </Text>
              <Text fontSize="xs" textColor="primary">
                {group.destinationAddress?.zip},{" "}
                {group.destinationAddress?.countryCode}
              </Text>
            </Flex>
          </Flex>
          <Flex>
            <Flex dir="column" gap="none">
              <Text fontSize="xs" textColor="tertiary">
                Ship from
              </Text>
              <Text fontSize="xs" textColor="primary">
                {group.originAddress?.name}
              </Text>
            </Flex>
          </Flex>
        </Flex>
        <RedoTable
          additionalClasses={styles.table}
          layout={tableProps.layout}
          rowsOfData={tableProps.rowsOfData}
        />
      </Flex>
    </Card>
  );
});
